import React from "react";
import { categoricalFill } from "./WorkspaceInitialsBadge.model";
import { Badge } from "./WorkspaceInitialsBadge.styles";
import type { WorkspaceInitialsBadgeProps } from "./WorkspaceInitialsBadge.types";

/**
 * Derive a 1–2 character initials string from a workspace name.
 * - "Marketing ops"        → "MO"
 * - "Product analytics"    → "PA"
 * - "Finance and controls" → "FC"  (first letter of first + last meaningful word)
 * - "People"               → "P"
 * - "  "  / ""             → "?"   (defensive fallback so the badge always has glyphs)
 * - "Café"                 → "C"   (accents stripped via NFD normalization)
 */
export const getWorkspaceInitials = (name: string): string => {
  if (!name) return "?";
  // Strip combining diacritics (U+0300–U+036F) after NFD decomposition.
  const normalized = name.normalize("NFD").replace(/[̀-ͯ]/g, "");
  const words = normalized.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.charAt(0).toUpperCase();
  const first = words[0]!.charAt(0);
  const last = words[words.length - 1]!.charAt(0);
  return (first + last).toUpperCase();
};

export const WorkspaceInitialsBadge: React.FC<WorkspaceInitialsBadgeProps> = ({
  name,
  initials,
  ariaLabel,
  size = "md",
  shape = "rounded",
  tone = "default",
  seed,
  decorative = false,
}) => {
  const text = (initials ?? getWorkspaceInitials(name)).slice(0, 3);
  const a11y = decorative
    ? { "aria-hidden": true }
    : { role: "img", "aria-label": ariaLabel ?? `${name} initials badge` };
  return (
    <Badge {...a11y} $size={size} $shape={shape} $tone={tone} $fill={tone === "categorical" ? categoricalFill(seed ?? name) : undefined}>
      {text}
    </Badge>
  );
};

export default WorkspaceInitialsBadge;
